import type { LocationQuery, RouteLocation } from 'vue-router'
import type {
  ListItem,
  WithContext,
  Article,
  BreadcrumbList,
  WebSite,
  Thing,
} from 'schema-dts'
import type { Meta } from '@unhead/schema'

const basePath = '/api/page/'

interface PageReq {
  path: string
  query: LocationQuery
}

export const usePage = () => {
  const { fetchPost, fetchGet } = useCustomFetch()
  const errorStore = useErrorStore()
  const pageStore = usePageStore()
  const { setUaO } = useUserStore()
  const { serviceType } = useServiceType()
  const { merge } = useDataStore()
  const putCondition = (value: PageAndCache) => {
    return !value?.page?.error
  }
  const pageCache = useFIFOCache<PageAndCache>(
    'page-path-cache',
    20,
    1000 * 60 * 10,
    putCondition,
  )

  async function getPageAndCache(): Promise<{ page: Page; meta: any }> {
    const route = useRoute()
    const { langPath } = useI18n()
    const { getTitle, hostUrl, siteName, getSearchOptions } = useSite()
    try {
      const page = pageStore.current
      const nonLangPath = getNonLangPath(page.path)
      const head: any = {
        script: [],
        style: [],
        link: [
          {
            rel: 'alternate',
            hreflang: 'ja',
            href: hostUrl() + nonLangPath,
          },
          {
            rel: 'alternate',
            hreflang: 'x-default',
            href: hostUrl() + normalizePath('/en' + nonLangPath),
          },
        ],
        meta: [
          {
            property: 'og:url',
            content: hostUrl() + normalizePath(page.path),
          },
          {
            property: 'og:locale',
            content: isEnPath(page.path) ? 'en_US' : 'ja_JP',
          },
        ],
      }
      if (page.error) {
        const errorTitle = errorStore.getErrorTitle()
        const title = getTitle({ ja: errorTitle, en: errorTitle })
        head.title = title
        head.meta.push({
          property: 'og:title',
          content: title,
        })
        return { page, meta: head }
      } else {
        const title = getTitle(page.title)
        head.title = title
        head.meta.push({
          property: 'og:title',
          content: title,
        })
        const meta = head.meta as Meta[]
        if (page.description) {
          meta.push(
            {
              name: 'description',
              content: page.description?.ja,
            },
            {
              property: 'og:description',
              content: page.description?.ja,
            },
          )
        }
        if (page.ogType) {
          meta.push({
            property: 'og:type',
            content: page.ogType,
          })
        } else {
          meta.push({
            property: 'og:type',
            content: 'article',
          })
        }
        if (page.twitterCard) {
          meta.push({
            name: 'twitter:card',
            content: page.twitterCard,
          })
        }
        let ogImage = page.imageUrl || `/assets/${serviceType}/og/${serviceType}.png`
        if (ogImage.startsWith('/')) {
          ogImage = hostUrl() + ogImage
        }
        meta.push({
          property: 'og:image',
          content: ogImage,
        })

        // 構造化データ
        const jsonld: WithContext<Thing>[] = []
        //　階層構造
        if (page.breadcrumbs?.length) {
          const breadcrumb = page.breadcrumbs[0]

          const itemListElement: ListItem[] = [
            {
              '@type': 'ListItem',
              position: 1,
              item: {
                '@id': hostUrl() + langPath('/'),
                name: siteName(),
              },
            },
          ]
          // TODO この処理は必要になりそうな気がするので何処かで確認する
          // if (breadcrumb.length > 0) {
          //   itemListElement.push(
          //     ...(breadcrumb.map((item, index) => {
          //       return {
          //         '@type': 'ListItem',
          //         position: index + 2,
          //         item: {
          //           '@id': hostUrl() + item.path,
          //           name: item.title,
          //         },
          //       }
          //     }) as any),
          //   )
          // }
          // if (!$isTop.value) {
          //   itemListElement.push({
          //     '@type': 'ListItem',
          //     position: breadcrumb.length + 2,
          //     item: {
          //       '@id': hostUrl() + page.path,
          //       name: page.title,
          //     },
          //   })
          // }

          const breadCrumbList: WithContext<BreadcrumbList> = {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement,
          }
          jsonld.push(breadCrumbList)
        }

        // WebSite
        const potentialAction = []
        const searchOptions = getSearchOptions()
        if (searchOptions?.length) {
          potentialAction.push({
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate:
                hostUrl() +
                searchOptions[searchOptions.length - 1].url +
                '{search_term_string}',
            },
            'query-input': 'required name=search_term_string',
          })
        }

        const website: WithContext<WebSite> = {
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: siteName(),
          url: hostUrl(),
          potentialAction,
        }
        jsonld.push(website)

        // Article
        const article: WithContext<Article> = {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: page.title?.ja,
          // image: [
          // ],
        }
        // TODO ここも必要かもしれないので何処かで確認する
        // if (page.lastModified) {
        //   article.datePublished =
        //     new IosDate(page.lastModified).toISOString() + '+09:00'
        // }
        jsonld.push(article)
        head.script.push({
          innerHTML: JSON.stringify(jsonld),
          type: 'application/ld+json',
        })

        // useHeadはComposable内では使えないので、メタデータとして返してsetup内で使う
        return { page, meta: head }
      }
    } catch (e) {
      console.error('get-page-and-cache-error', e, route.path, route.query)
      return null
    }
  }

  const getPageByPath = (route: RouteLocation): Promise<PageAndCache> => {
    return fetchPost<PageReq, PageAndCache>('/api/page/path', {
        path: route.path,
        query: mapToListMap(route.query),
      }
    )
  }

  function getPage(id: string): Promise<Page> {
    return fetchGet(basePath + id)
  }

  function createPage(page: Page): Promise<Page> {
    return fetchPost(basePath + 'create', page)
  }

  function updatePage(page: Page): Promise<Page> {
    return fetchPost(basePath + 'update', page)
  }

  function deletePage(id: string): Promise<Page> {
    return fetchPost(basePath + id + '/delete')
  }

  function searchPage(query: SearchQuery): Promise<SearchResult<Page>> {
    return fetchGet(basePath + 'search' + toSearchQueryString(query))
  }

  const updatePageTitle = (meta: any, pageTitle: string) => {
    meta.title = pageTitle
    let newMeta = meta.meta.filter(obj => obj.property !== 'og:title')
    newMeta.push({
      property: 'og:title',
      content: pageTitle,
    })
    meta.meta = newMeta
  }

  const updateLastBreadcrumbs = (page: Page, pageTitle: LangString = undefined, pageUrl: string = undefined) => {
    if (pageTitle) {
      page.breadcrumbs[page.breadcrumbs.length - 1].title = pageTitle
    }
    if (pageUrl) {
      page.breadcrumbs[page.breadcrumbs.length - 1].path = pageUrl
    }
  }

  const updatePrevBreadcrumbs = (page: Page, pageTitle: LangString = undefined, pageUrl: string = undefined) => {
    if (pageTitle) {
      page.breadcrumbs[page.breadcrumbs.length - 2].title = pageTitle
    }
    if (pageUrl) {
      page.breadcrumbs[page.breadcrumbs.length - 2].path = pageUrl
    }
  }

  return {
    getPageByPath,
    getPage,
    createPage,
    updatePage,
    deletePage,
    searchPage,
    getPageAndCache,
    updatePageTitle,
    updateLastBreadcrumbs,
    updatePrevBreadcrumbs,
  }
}
